import Vue from 'vue'
import commonMutations from '@/store/ui/common/mutations'
import { concat, get, isEmpty, isNil, findIndex, find } from 'lodash'
import helpers from './helpers'

const mutations = {
    ...commonMutations,

    resetTaxoCountingData(state) {
        Vue.set(state.counters, 'created', 0)
        Vue.set(state.counters, 'updated', 0)
        Vue.set(state.counters, 'deleted', 0)
        Vue.set(state.counters, 'dragged', 0)
        Vue.set(state, 'updatedSegmentIds', [])
        Vue.set(state, 'createdSegmentKeys', [])
    },

    resetStoreOnLeave(state) {
        Vue.set(state, 'searchedCustomSegmentParents', [])
        Vue.set(state, 'isSearching', false)
        Vue.set(state, 'displayingSearch', false)
        Vue.set(state, 'selectedSegmentId', null)
    },

    setUpdatedSegmentIds(state, { segmentId }) {
        const index = state.updatedSegmentIds.length
        Vue.set(state.updatedSegmentIds, index, segmentId)
    },

    setCreatedSegmentKeys(state, { key }) {
        const index = state.createdSegmentKeys.length
        Vue.set(state.createdSegmentKeys, index, key)
    },

    setRetrievedItems(state, { id, items }) {
        const _hierarchies = {
            ...state.hierarchies,
            [id]: items
        }
        Vue.set(state, 'hierarchies', _hierarchies)
    },

    setDragged(state, { nodeId }) {
        const node = helpers.getNode(state.hierarchies[state.selectedTaxoId], nodeId)
        Vue.set(node, 'isDragged', true)
    },

    setUpdateList(state, { parentId, items }) {
        if (items) {
            if (!parentId) {
                Vue.set(state.hierarchies, `${state.selectedTaxoId}`, items)
            } else {
                const parent = helpers.getNode(state.hierarchies[state.selectedTaxoId], parentId)
                if (parent && !isEmpty(parent)) {
                    Vue.set(parent, 'children', items)
                }
            }
        }
    },

    setParentId(state, { item, parentId }) {
        if (item) {
            const node = helpers.getNode(state.hierarchies[state.selectedTaxoId], item.id)
            Vue.set(node, 'parent_id', parentId)
        }
    },

    setAddItem(state, { item, parentId }) {
        if (item && parentId) {
            const parent = helpers.getNode(state.hierarchies[state.selectedTaxoId], parentId)
            if (parent && !isEmpty(parent)) {
                const _children = concat(item, parent.children)
                Vue.set(parent, 'children', _children)
            }
        }
    },

    setRuleStatus(state, { itemId, status }) {
        const segment = helpers.getNode(state.hierarchies[state.selectedTaxoId], itemId)
        if (segment && !isNil(get(segment, 'rule', null))) {
            segment.rule.status = status
        }
    },

    setSelectedTaxoId(state, { id }) {
        Vue.set(state, 'selectedTaxoId', id)
    },

    setRuleEstimation(state, { estimation, segmentId }) {
        Vue.set(state.ruleEstimations, segmentId, estimation)
    },

    setIsSearching(state, { isSearching }) {
        Vue.set(state, 'isSearching', isSearching)
    },

    setDisplayingSearch(state, { isDisplaying }) {
        Vue.set(state, 'displayingSearch', isDisplaying)
    },

    setParents(state, { parents }) {
        Vue.set(state, 'searchedCustomSegmentParents', parents)
    },

    setIsRuleValid(state, { isValid }) {
        Vue.set(state, 'isRuleValid', isValid)
    },

    setBooleanFieldItem(state, { id, field }) {
        state.list = state.list.map(item => {
            item[field] = parseInt(item.id) === parseInt(id) ? !item[field] : false
            return item
        })
    },

    setSelectedSegmentId(state, { id }) {
        Vue.set(state, 'selectedSegmentId', id)
    },

    setNewCSParentId(state, { id }) {
        Vue.set(state, 'newCSParentId', id)
    },

    setNewCSSiblings(state, { siblings }) {
        Vue.set(state, 'newCSSiblings', siblings)
    },

    setSegmentForm(state, { displayModal, type, activeTab }) {
        Vue.set(state.formSegment, 'displayModal', displayModal)
        Vue.set(state.formSegment, 'type', type)
        Vue.set(state.formSegment, 'activeTab', activeTab)
    },

    setCustomSegmentData(state, { action, item, canEditKey = false }) {
        if (item) {
            const id = isNil(item.id) ? item.key : item.id
            const node = helpers.getNode(state.hierarchies[state.selectedTaxoId], id)

            // DELETE
            if (action === 'delete') {
                node.action = action

                if (!isEmpty(node.children)) {
                    const childrenNodes = helpers.getNodeChildren(node.children, item.id)
                    for (const child of childrenNodes) {
                        child.action = action
                    }
                }
            }

            // EDIT
            if (action === 'update') {
                Vue.set(node, 'label', item.label)
                Vue.set(node, 'bootstrap_status', item.bootstrap_status)
                Vue.set(node, 'action', action)
                Vue.set(node, 'smart_segments', item.smart_segments)
                if (canEditKey) Vue.set(node, 'key', item.key)
                item.rule ? Vue.set(node, 'rule', item.rule) : Vue.delete(node, 'rule')
            }
        }
    },

    updateTaxoCounters(state, { item, action, childrenCount }) {
        var currentCount = state.counters[item]
        if (item !== 'deleted') {
            if (action === 'increase') {
                Vue.set(state.counters, item, (currentCount += 1))
            } else if (action === 'decrease') {
                Vue.set(state.counters, item, (currentCount -= 1))
            }
        } else {
            const deleteChildrenCount = childrenCount[0]
            const createChildrenCount = childrenCount[1]
            const updateChildrenCount = childrenCount[2]

            if (action === 'increase') {
                Vue.set(state.counters, item, (currentCount += deleteChildrenCount + 1))
                Vue.set(state.counters, 'created', (state.counters['created'] -= createChildrenCount))
                Vue.set(state.counters, 'updated', (state.counters['updated'] -= updateChildrenCount))
            } else if (action === 'decrease') {
                Vue.set(state.counters, item, (currentCount -= deleteChildrenCount + 1))
                Vue.set(state.counters, 'created', (state.counters['created'] += createChildrenCount))
                Vue.set(state.counters, 'updated', (state.counters['updated'] += updateChildrenCount))
            }
        }
    },

    setTaxoCounter(state, { type, count }) {
        Vue.set(state.counters, type, count)
    },

    setSegmentAction(state, { item, action }) {
        const id = isNil(item.id) ? item.key : item.id
        const node = helpers.getNode(state.hierarchies[state.selectedTaxoId], id)
        Vue.set(node, 'action', action)
    },

    setActiveTabManageTaxo(state, { tab }) {
        Vue.set(state, 'activeTab', tab)
    },

    setDisplaySharingTaxonomyModal(state, { display }) {
        Vue.set(state, 'displaySharingTaxonomyModal', display)
    },
    setSelectedSharingTaxonomy(state, { taxo }) {
        Vue.set(state, 'selectedSharingTaxonomy', taxo)
    },

    setTaxonomyForm(state, { displayModal, type }) {
        Vue.set(state.formTaxonomy, 'displayModal', displayModal)
        Vue.set(state.formTaxonomy, 'type', type)
    },
    setIsProcessing(state, { value }) {
        Vue.set(state, 'isProcessing', value)
    },
    setSegmentBootstrapAlreadyActivated(state, { taxo: taxoId, id, value }) {
        const hierarchies = state.hierarchies
        const hierarchy = hierarchies[taxoId]
        if (hierarchy) {
            const segment = find(hierarchy, item => item.id === id)
            if (segment) {
                const segmentIndex = findIndex(hierarchy, item => item.id === id)
                segment.bootstrapAlreadyActivated = value
                hierarchy.splice(segmentIndex, 1, segment)
                hierarchies[taxoId] = hierarchy
                Vue.set(state, 'hierarchies', hierarchies)
            }
        }
    },
    setHasCounterActivated(state, { value }) {
        Vue.set(state, 'hasCounterActivated', value)
    }
}

export default mutations
